import { useState, useCallback, useMemo } from 'react';
import { api, DfeDefinitionSummary } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useItemDefinitions } from '../hooks/useItemDefinitions';
import { useMintHistory } from '../hooks/useMintHistory';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { ItemDefinitionForm } from '../components/ItemDefinitionForm';
import { MintItemDialog } from '../components/MintItemDialog';
import { MintHistoryList } from '../components/MintHistoryList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Coins,
  AlertCircle,
  Search,
  Pencil,
  Archive,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

export function AdminPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(
    () => user?.roles?.some((r) => r === 'admin' || r === 'litcraft_admin') ?? false,
    [user?.roles],
  );

  const { search, debouncedSearch, handleSearchChange } = useDebouncedSearch();

  const { definitions, total, loading, error, refresh, page, totalPages, goToPage } =
    useItemDefinitions({
      search: debouncedSearch || undefined,
      pageSize: 50,
    });

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDef, setEditingDef] = useState<DfeDefinitionSummary | null>(null);
  const [showMintDialog, setShowMintDialog] = useState(false);

  const openCreateForm = useCallback(() => {
    setEditingDef(null);
    setShowCreateForm(true);
  }, []);

  const openEditForm = useCallback((def: DfeDefinitionSummary) => {
    setShowCreateForm(false);
    setEditingDef(def);
  }, []);

  const handleCreate = useCallback(
    async (values: Record<string, unknown>) => {
      await api.createDfeDefinition(
        values as {
          name: string;
          description?: string;
          fungibility: string;
          thumbnailUri?: string;
          imageUri?: string;
          metadata?: Record<string, unknown>;
          maxSupply?: number | null;
          status?: string;
        },
      );
      setShowCreateForm(false);
      refresh();
    },
    [refresh],
  );

  const handleUpdate = useCallback(
    async (values: Record<string, unknown>) => {
      if (!editingDef) return;
      await api.updateDfeDefinition(editingDef.itemDefId, values);
      setEditingDef(null);
      refresh();
    },
    [editingDef, refresh],
  );

  const handleArchive = useCallback(
    async (def: DfeDefinitionSummary) => {
      if (!confirm(`Archive "${def.name}"? This will hide it from public listings.`)) return;
      try {
        await api.archiveDfeDefinition(def.itemDefId);
        refresh();
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to archive item');
      }
    },
    [refresh],
  );

  const handleMint = useCallback(
    async (input: {
      itemDefId: string;
      toUsername: string;
      quantity: number;
      metadata?: Record<string, unknown>;
      notes?: string;
    }) => {
      await api.mintDfeItems(input);
      refresh();
    },
    [refresh],
  );

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You do not have admin permissions to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">DFE Admin</h1>

      <Tabs defaultValue="definitions">
        <TabsList>
          <TabsTrigger value="definitions">Definitions</TabsTrigger>
          <TabsTrigger value="mint">Mint</TabsTrigger>
          <TabsTrigger value="mintHistory">Mint History</TabsTrigger>
        </TabsList>

        <TabsContent value="definitions" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Item Definitions</h2>
            <Button size="sm" onClick={openCreateForm}>
              <Plus className="h-4 w-4 mr-1" />
              New Definition
            </Button>
          </div>

          {showCreateForm && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">Create Item Definition</CardTitle>
              </CardHeader>
              <CardContent>
                <ItemDefinitionForm
                  onSubmit={handleCreate}
                  onCancel={() => setShowCreateForm(false)}
                />
              </CardContent>
            </Card>
          )}

          {editingDef && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">Edit: {editingDef.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <ItemDefinitionForm
                  isEdit
                  initialValues={{
                    name: editingDef.name,
                    description: editingDef.description || '',
                    fungibility: editingDef.fungibility,
                    thumbnailUri: editingDef.thumbnailUri || '',
                    imageUri: editingDef.imageUri || '',
                    maxSupply: editingDef.maxSupply,
                    marketCategory: editingDef.marketCategory || '',
                    metadata: JSON.stringify(editingDef.metadata, null, 2),
                  }}
                  onSubmit={handleUpdate}
                  onCancel={() => setEditingDef(null)}
                />
              </CardContent>
            </Card>
          )}

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search definitions..."
              className="pl-9"
            />
          </div>

          {error && <p className="text-destructive mb-4">{error}</p>}

          {loading && definitions.length === 0 ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : definitions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {debouncedSearch ? 'No definitions match your search.' : 'No definitions yet.'}
            </p>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead className="w-[140px]">Market Category</TableHead>
                      <TableHead className="w-[120px]">Supply</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                      <TableHead className="w-[80px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {definitions.map((def) => (
                      <TableRow key={def.itemDefId}>
                        <TableCell className="font-medium">{def.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {def.fungibility === 'fungible' ? 'Fungible' : 'NFT'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {def.marketCategory || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {def.currentSupply}
                          {def.maxSupply ? ` / ${def.maxSupply}` : ''}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={def.status === 'active' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {def.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => openEditForm(def)}
                              aria-label={`Edit ${def.name}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {def.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleArchive(def)}
                                aria-label={`Archive ${def.name}`}
                              >
                                <Archive className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  {total} definition{total !== 1 ? 's' : ''}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => goToPage(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => goToPage(page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="mint" className="mt-4">
          <div className="text-center py-12">
            <Coins className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">Mint Items</h2>
            <p className="text-muted-foreground mb-4">Create new items and assign them to users.</p>
            <Button onClick={() => setShowMintDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Mint Items
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="mintHistory" className="mt-4">
          <MintHistoryContent />
        </TabsContent>
      </Tabs>

      <MintItemDialog
        open={showMintDialog}
        onClose={() => setShowMintDialog(false)}
        onConfirm={handleMint}
      />
    </div>
  );
}

function MintHistoryContent() {
  const { entries, total, loading, error, loadMore, hasMore } = useMintHistory();

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Mint History</h2>
        <p className="text-sm text-muted-foreground">
          {total} record{total !== 1 ? 's' : ''}
        </p>
      </div>
      {error && <p className="text-destructive mb-4">{error}</p>}
      <MintHistoryList
        entries={entries}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
      />
    </div>
  );
}
